/**
 * Русские числительные — одно место на весь кабинет.
 *
 * ЗАЧЕМ ЭТОТ ФАЙЛ ПОЯВИЛСЯ. Правило склонения было списано в пяти файлах
 * (`journal/consequences.ts`, `screens/master/master-text.ts`, `catalog/catalog-model.ts`,
 * `onboarding/checklist-model.ts`, `shell/timezone-note.ts`) — с разными сигнатурами
 * и разными телами. Следствие предсказуемое: когда в шестом месте понадобилось
 * «7 визитов», никто не нашёл, что импортировать, и написал слово руками. Так в
 * интерфейс попали «1 визитов», «1 неявок», «1 услуг» и «1 записями» — восемь строк,
 * которые видит человек за стойкой каждый день.
 *
 * Поэтому здесь не «утилита на всякий случай», а единственный экземпляр правила
 * и готовые подписи для величин, которые в кабинете считают чаще всего.
 *
 * Intl.PluralRules не используется намеренно: он отвечает, КАКАЯ форма нужна
 * («one», «few», «many»), но сами формы всё равно приходится перечислять. Три слова
 * в вызове честнее, чем таблица правил, спрятанная за именем стандарта.
 */

/**
 * «визит» / «визита» / «визитов» — выбор формы по числу.
 *
 * Дробное число округляется: «2.5 визита» не бывает, а вот длительность в часах
 * в подпись попадает, и `NaN` в родительном падеже выглядит хуже нуля.
 */
export function plural(count: number, one: string, few: string, many: string): string {
  const abs = Math.abs(Math.round(count))
  const mod100 = abs % 100
  if (mod100 >= 11 && mod100 <= 14) return many
  const mod10 = abs % 10
  if (mod10 === 1) return one
  if (mod10 >= 2 && mod10 <= 4) return few
  return many
}

/** То же самое вместе с числом: «12 визитов», «4 визита», «1 визит». */
export function countOf(count: number, one: string, few: string, many: string): string {
  return `${String(Math.round(count))} ${plural(count, one, few, many)}`
}

/** «12 визитов», «4 визита», «1 визит». */
export function visitsLabel(count: number): string {
  return countOf(count, "визит", "визита", "визитов")
}

/** «0 неявок», «1 неявка», «2 неявки». */
export function noShowLabel(count: number): string {
  return countOf(count, "неявка", "неявки", "неявок")
}

/** «6 услуг», «2 услуги», «1 услуга». */
export function servicesLabel(count: number): string {
  return countOf(count, "услуга", "услуги", "услуг")
}

/** «6 записей», «2 записи», «1 запись». */
export function appointmentsLabel(count: number): string {
  return countOf(count, "запись", "записи", "записей")
}

/**
 * Форма ПОСЛЕ ПРЕДЛОГА, требующего творительного падежа: «с 1 записью», «с 3 записями».
 *
 * Отдельная функция, потому что обычное склонение по числу здесь не годится:
 * «с 1 записями» — не по-русски, а именно эта конструкция стоит в заголовке диалога
 * об отпуске мастера.
 */
export function appointmentsInstrumental(count: number): string {
  return countOf(count, "записью", "записями", "записями")
}

/** «1 минута», «3 минуты», «10 минут». */
export function minutesLabel(count: number): string {
  return countOf(count, "минута", "минуты", "минут")
}

/**
 * Форма ПОСЛЕ ПРЕДЛОГА «из»: «из 1 визита», «из 2 визитов», «из 5 визитов».
 *
 * Отдельно от `visitsLabel`, потому что предлог требует родительного падежа, и обычный
 * выбор по числу даёт «3 из 1 визит» — строку, которую видно с другого конца зала.
 */
export function visitsGenitive(count: number): string {
  return countOf(count, "визита", "визитов", "визитов")
}
